import { call, put, takeLatest } from "redux-saga/effects";
import {
  deleteAccountAddressApi,
  getAccountSavedAddressesApi,
  getEditSavedAddressApi,
  updateAccountAddressApi,
} from "../../../api/myAccount";
import {
  failedToDeleteAccountAddress,
  failedToFetchAccountAddressList,
  failedToFetchAccountEditAddress,
  failedToSaveAccountAddress,
  requestToDeleteAccountAddress,
  requestToFetchAccountAddressList,
  requestToFetchAccountEditAddress,
  requestToSaveAccountAddress,
  responseFromDeleteAccountAddress,
  responseFromFetchAccountAddressList,
  responseFromFetchAccountEditAddress,
  responseFromSaveAccountAddress,
} from "../../slices/accountAddressBook";
import { normalizeAccountAddressListItem } from "../../../containers/myAccountPage/sections/addressBook/accountAddressHelpers";

/**
 * @param {object} response
 * @returns {Array|null}
 */
function parseAddressList(response) {
  const list = response?.data?.addressList ?? response?.addressList ?? response?.data?.data?.addressList;
  return Array.isArray(list) ? list : null;
}

/**
 * @param {object} detail
 * @returns {object}
 */
function mapEditAddressDetails(detail) {
  const countryGeoId = detail.countryGeoId || "IND";
  const intlZip = detail.intlpostalcode || detail.address2;
  const addressDetails = {
    salutation: detail.salutation,
    name: detail.toName,
    address: detail.address1,
    city: detail.city,
    postalCode: detail.postalCode,
    address2: countryGeoId === "IND" ? detail.address2 : intlZip,
    intlpostalcode: detail.intlpostalcode || intlZip,
    mobileCode: detail.teleCode,
    mobileNumber: detail.mobileNumber,
    altMobileNumber: detail.altRecpNumber,
    email: detail.email,
    addressType: detail.addressType,
    countryGeoId,
    id: detail.contactMechId,
    doorBell: detail.doorBell,
    landmark: detail.landmark,
    houseNo: detail.houseNo,
    flatHouseNo: detail.flatHouseNo,
    directions: detail.areaLocality,
    localityArea: countryGeoId === "IND" ? detail.address2 : intlZip,
    postalCodeExt: detail.postalCodeExt,
    address3: detail.houseNo,
  };
  if (Object.prototype.hasOwnProperty.call(detail, "addressId")) {
    addressDetails.addressId = detail.addressId;
    addressDetails.houseNo = detail.houseNo;
    addressDetails.landmark = detail.landmark;
  }
  return addressDetails;
}

/**
 * @param {object} addressDetail
 * @param {boolean} isEdit
 * @returns {Record<string, string>}
 */
function buildSaveParams(addressDetail, isEdit) {
  const params = {
    postalAddress: isEdit ? addressDetail.id : "noPartyPostal",
    toName: addressDetail.name,
    address: addressDetail.address,
    city: addressDetail.city,
    countryGeoId: addressDetail.countryGeoId || "IND",
    contactMechPurpse: "SHIPPING_LOCATION",
    postalcode: addressDetail.postalCode,
    countryCode: addressDetail.mobileCode || "91",
    phone: addressDetail.mobileNumber,
    telecomPurposeId: "PHONE_SHIPPING",
    altPhone: addressDetail.altMobileNumber,
    altTelecomPurposeId: "PHONE_SHIPPING",
    addressType: addressDetail.addressType || "other",
    attnName: addressDetail.email,
    address2: addressDetail.localityArea || (addressDetail.address2 && addressDetail.address2.trim()),
    latitude: addressDetail.latitude,
    longitude: addressDetail.longitude,
    doorBellName: addressDetail.doorBell,
    intlpostalcode: addressDetail.intlpostalcode,
    stateProvinceGeoId: addressDetail.stateProvinceGeoId || "_NA_",
    addrSalutation: addressDetail.salutation || "",
    flatHouseNo: addressDetail.flatHouseNo,
    directions: addressDetail.directions,
    landmark: addressDetail.landmark,
  };
  if (Object.prototype.hasOwnProperty.call(addressDetail, "addressId")) {
    params.address2 = "";
    params.addressId = addressDetail.addressId;
    params.houseNo = addressDetail.houseNo;
    params.landmark = addressDetail.landmark;
  }
  return params;
}

/**
 * @generator
 */
function* fetchAccountAddressListSaga() {
  try {
    const res = yield call(getAccountSavedAddressesApi);
    const list = parseAddressList(res);
    if (list) {
      yield put(responseFromFetchAccountAddressList(list.map(normalizeAccountAddressListItem)));
    } else if (res?.isSuccess === false) {
      yield put(failedToFetchAccountAddressList());
    } else {
      yield put(responseFromFetchAccountAddressList([]));
    }
  } catch {
    yield put(failedToFetchAccountAddressList());
  }
}

/**
 * @param {{ payload?: { contactMechId?: string } }} action
 */
function* deleteAccountAddressSaga(action) {
  try {
    const contactMechId = action.payload?.contactMechId;
    const res = yield call(deleteAccountAddressApi, contactMechId);
    const data = res?.data ?? res;
    if (data?.isSuccess || res?.isSuccess) {
      yield put(
        responseFromDeleteAccountAddress({
          addressId: data?.contactMechId ?? contactMechId,
        }),
      );
      yield put(requestToFetchAccountAddressList());
    } else {
      yield put(failedToDeleteAccountAddress());
    }
  } catch {
    yield put(failedToDeleteAccountAddress());
  }
}

/**
 * @param {{ payload?: { contactMechId?: string } }} action
 */
function* fetchAccountEditAddressSaga(action) {
  try {
    const contactMechId = action.payload?.contactMechId;
    const res = yield call(getEditSavedAddressApi, contactMechId);
    const detail = res?.data?.data ?? res?.data ?? res;
    if (detail?.contactMechId || detail?.toName) {
      yield put(responseFromFetchAccountEditAddress(mapEditAddressDetails(detail)));
    } else {
      yield put(failedToFetchAccountEditAddress());
    }
  } catch {
    yield put(failedToFetchAccountEditAddress());
  }
}

/**
 * @param {{ payload?: { addressDetail?: object, isEdit?: boolean } }} action
 */
function* saveAccountAddressSaga(action) {
  try {
    const { addressDetail, isEdit, apiParams } = action.payload ?? {};
    const params = apiParams ?? buildSaveParams(addressDetail ?? {}, Boolean(isEdit));
    const res = yield call(updateAccountAddressApi, params);
    if (res?.isSuccess || res?.data?.isSuccess) {
      yield put(responseFromSaveAccountAddress());
      yield put(requestToFetchAccountAddressList());
    } else {
      yield put(failedToSaveAccountAddress());
    }
  } catch {
    yield put(failedToSaveAccountAddress());
  }
}

/**
 * @generator
 */
export default function* watchAccountAddressBookSaga() {
  yield takeLatest(requestToFetchAccountAddressList.type, fetchAccountAddressListSaga);
  yield takeLatest(requestToDeleteAccountAddress.type, deleteAccountAddressSaga);
  yield takeLatest(requestToFetchAccountEditAddress.type, fetchAccountEditAddressSaga);
  yield takeLatest(requestToSaveAccountAddress.type, saveAccountAddressSaga);
}
